const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const money = value => {
  const number = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = value => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
}).format(money(value));

const normalizeStage = customer => {
  const raw = String(customer?.acquisitionStatus || customer?.status || '').toLowerCase();
  return raw.includes('acquired') || raw.includes('purchased') || raw.includes('completed') ? 'acquired' : raw;
};

const getToken = () => localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || '';

const getDealerId = () => {
  try {
    const keys = ['dealership', 'dealer', 'vincentrix_dealer'];
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.id) return parsed.id;
    }
  } catch (error) {
    console.warn('VinCentrix ROI dealer lookup failed:', error);
  }
  return '';
};

const authHeaders = () => {
  const token = getToken();
  const dealerId = getDealerId();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(dealerId ? { 'X-Dealership-Id': dealerId } : {})
  };
};

const daysBetween = (start, end) => {
  if (!start || !end) return null;
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.max(0, Math.round((b - a) / 86400000));
};

const acquiredDate = customer => customer.acquiredAt || customer.acquisitionCompletedAt || customer.updatedAt || customer.createdAt || '';

const getAttribution = customer => {
  if (customer.acquisitionAttribution) return customer.acquisitionAttribution;
  if (customer.sydneyPipelineAutomated) return 'Sydney + VinCentrix';
  return 'VinCentrix';
};

const metricCard = (label, value, subtext = '') => `
  <div class="vc-roi-card">
    <div class="vc-roi-label">${label}</div>
    <div class="vc-roi-value">${value}</div>
    ${subtext ? `<div class="vc-roi-sub">${subtext}</div>` : ''}
  </div>`;

const vehicleName = customer => customer.vehicle || [customer.vehicleYear, customer.vehicleMake, customer.vehicleModel, customer.vehicleTrim].filter(Boolean).join(' ') || 'Vehicle';

let customersCache = [];
let openEditorId = null;
let refreshTimer = null;

const saveCustomer = async (id, updates) => {
  const response = await fetch(`${API_URL}/api/customers/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error(`Save failed (${response.status})`);
  return response.json();
};

const fetchCustomers = async () => {
  const response = await fetch(`${API_URL}/api/customers`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Unable to load acquisition ROI (${response.status})`);
  const data = await response.json();
  return data.customers || [];
};

const ensureStyles = () => {
  if (document.getElementById('vc-acquisition-roi-styles')) return;
  const style = document.createElement('style');
  style.id = 'vc-acquisition-roi-styles';
  style.textContent = `
    .vc-roi-wrap{margin:0 0 16px;border:1px solid rgba(34,197,94,.24);border-radius:18px;background:linear-gradient(135deg,rgba(8,20,36,.96),rgba(7,14,28,.96));box-shadow:0 14px 38px rgba(0,0,0,.28);overflow:hidden;color:#fff;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
    .vc-roi-head{padding:18px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;border-bottom:1px solid rgba(255,255,255,.07)}
    .vc-roi-kicker{font-size:10px;letter-spacing:1.8px;text-transform:uppercase;color:#4ade80;font-weight:900;margin-bottom:5px}
    .vc-roi-title{font-size:18px;font-weight:800}.vc-roi-note{font-size:11px;color:rgba(255,255,255,.42);margin-top:4px}
    .vc-roi-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;padding:14px}
    .vc-roi-card{padding:14px;border-radius:13px;background:rgba(255,255,255,.035);border:1px solid rgba(255,255,255,.075)}
    .vc-roi-label{font-size:9px;text-transform:uppercase;letter-spacing:1px;color:rgba(255,255,255,.42);font-weight:800}.vc-roi-value{font-size:21px;font-weight:900;margin-top:5px}.vc-roi-sub{font-size:9px;color:rgba(255,255,255,.35);margin-top:4px}
    .vc-roi-units{padding:0 14px 14px}.vc-roi-row{display:grid;grid-template-columns:2fr repeat(6,1fr) 92px;gap:8px;align-items:center;padding:10px 12px;border-top:1px solid rgba(255,255,255,.055);font-size:10px}.vc-roi-row:first-child{border-top:0}.vc-roi-row strong{font-size:11px}.vc-roi-muted{color:rgba(255,255,255,.42)}
    .vc-roi-btn{border:1px solid rgba(96,165,250,.24);background:rgba(59,130,246,.11);color:#bfdbfe;border-radius:8px;padding:7px 9px;font-size:10px;font-weight:800;cursor:pointer}.vc-roi-refresh{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:#dbeafe;border-radius:9px;padding:8px 10px;font-size:10px;font-weight:800;cursor:pointer}
    .vc-roi-modal{position:fixed;inset:0;z-index:1800;background:rgba(0,0,0,.68);backdrop-filter:blur(7px);display:flex;align-items:center;justify-content:center;padding:18px}.vc-roi-editor{width:min(620px,96vw);max-height:90vh;overflow:auto;background:linear-gradient(180deg,#07101f,#050b15);border:1px solid rgba(96,165,250,.25);border-radius:18px;box-shadow:0 30px 80px rgba(0,0,0,.6);padding:20px;color:#fff}.vc-roi-fields{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:16px}.vc-roi-field label{display:block;font-size:10px;color:rgba(255,255,255,.48);margin-bottom:5px}.vc-roi-field input,.vc-roi-field select{width:100%;box-sizing:border-box;padding:11px;border-radius:9px;border:1px solid rgba(255,255,255,.11);background:#0b1322;color:#fff;font-size:12px}.vc-roi-actions{display:flex;justify-content:flex-end;gap:9px;margin-top:18px}.vc-roi-save{border:0;background:linear-gradient(135deg,#22c55e,#16a34a);color:#fff;border-radius:9px;padding:10px 14px;font-weight:900;cursor:pointer}.vc-roi-close{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);color:#fff;border-radius:9px;padding:10px 14px;font-weight:800;cursor:pointer}
    @media(max-width:768px){.vc-roi-grid{grid-template-columns:1fr 1fr}.vc-roi-row{grid-template-columns:1fr 1fr}.vc-roi-row>div:nth-child(n+6){display:none}.vc-roi-fields{grid-template-columns:1fr}.vc-roi-head{align-items:flex-start;flex-direction:column}}
  `;
  document.head.appendChild(style);
};

const renderEditor = customer => {
  document.querySelector('.vc-roi-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'vc-roi-modal';
  modal.innerHTML = `
    <div class="vc-roi-editor">
      <div class="vc-roi-kicker">Acquired Vehicle ROI</div>
      <div class="vc-roi-title">${vehicleName(customer)}</div>
      <div class="vc-roi-note">${customer.name || 'Unknown owner'}${customer.vin ? ` • ${customer.vin}` : ''}</div>
      <div class="vc-roi-fields">
        <div class="vc-roi-field"><label>Purchase Price</label><input data-field="purchasePrice" inputmode="decimal" value="${money(customer.purchasePrice) || ''}"></div>
        <div class="vc-roi-field"><label>Expected Retail Value</label><input data-field="expectedRetailValue" inputmode="decimal" value="${money(customer.expectedRetailValue) || ''}"></div>
        <div class="vc-roi-field"><label>Expected Gross</label><input data-field="expectedGross" inputmode="decimal" value="${money(customer.expectedGross) || Math.max(0,money(customer.expectedRetailValue)-money(customer.purchasePrice)) || ''}"></div>
        <div class="vc-roi-field"><label>Acquired Date</label><input data-field="acquiredAt" type="date" value="${acquiredDate(customer) ? new Date(acquiredDate(customer)).toISOString().slice(0,10) : ''}"></div>
        <div class="vc-roi-field"><label>Source</label><input data-field="source" value="${customer.source || ''}"></div>
        <div class="vc-roi-field"><label>Attribution</label><select data-field="acquisitionAttribution"><option ${getAttribution(customer)==='VinCentrix'?'selected':''}>VinCentrix</option><option ${getAttribution(customer)==='Sydney + VinCentrix'?'selected':''}>Sydney + VinCentrix</option><option ${getAttribution(customer)==='Manual'?'selected':''}>Manual</option></select></div>
      </div>
      <div class="vc-roi-actions"><button class="vc-roi-close">Cancel</button><button class="vc-roi-save">Save ROI</button></div>
      <div class="vc-roi-note vc-roi-save-status" style="margin-top:8px"></div>
    </div>`;
  document.body.appendChild(modal);
  modal.querySelector('.vc-roi-close').onclick = () => modal.remove();
  modal.onclick = event => { if (event.target === modal) modal.remove(); };
  modal.querySelector('.vc-roi-save').onclick = async () => {
    const fields = {};
    modal.querySelectorAll('[data-field]').forEach(input => { fields[input.dataset.field] = input.value; });
    const purchasePrice = money(fields.purchasePrice);
    const expectedRetailValue = money(fields.expectedRetailValue);
    const expectedGross = money(fields.expectedGross) || Math.max(0, expectedRetailValue - purchasePrice);
    const originalCreatedAt = customer.createdAt || '';
    const acquiredAt = fields.acquiredAt ? new Date(`${fields.acquiredAt}T12:00:00`).toISOString() : (customer.acquiredAt || new Date().toISOString());
    const daysToAcquire = daysBetween(originalCreatedAt, acquiredAt);
    const history = Array.isArray(customer.acquisitionHistory) ? customer.acquisitionHistory : [];
    const event = {
      id: `roi-${Date.now()}`,
      type: 'roi-updated',
      actor: 'user',
      actorLabel: 'Acquisition Team',
      title: 'Acquisition ROI updated',
      detail: `Purchase ${formatMoney(purchasePrice)} • Expected retail ${formatMoney(expectedRetailValue)} • Expected gross ${formatMoney(expectedGross)}`,
      timestamp: new Date().toISOString()
    };
    const status = modal.querySelector('.vc-roi-save-status');
    status.textContent = 'Saving…';
    try {
      const result = await saveCustomer(customer.id, {
        acquisitionStatus: 'acquired',
        purchasePrice,
        expectedRetailValue,
        expectedGross,
        acquiredAt,
        daysToAcquire,
        source: fields.source,
        acquisitionAttribution: fields.acquisitionAttribution,
        acquisitionHistory: [...history, event],
        acquisitionUpdatedAt: new Date().toISOString()
      });
      const saved = result.customer || { ...customer, ...fields, purchasePrice, expectedRetailValue, expectedGross, acquiredAt, daysToAcquire };
      customersCache = customersCache.map(item => item.id === customer.id ? saved : item);
      status.textContent = 'Saved';
      setTimeout(() => { modal.remove(); renderROI(); }, 350);
    } catch (error) {
      status.textContent = error.message || 'Unable to save ROI.';
    }
  };
};

const renderROI = () => {
  ensureStyles();
  const workspace = document.querySelector('.vc-acquisition-workspace');
  if (!workspace) return;
  let root = document.getElementById('vc-acquisition-roi');
  if (!root) {
    root = document.createElement('section');
    root.id = 'vc-acquisition-roi';
    root.className = 'vc-roi-wrap';
    const header = workspace.querySelector('.vc-acquisition-header');
    if (header?.nextSibling) workspace.insertBefore(root, header.nextSibling); else workspace.prepend(root);
  }

  const acquired = customersCache.filter(customer => normalizeStage(customer) === 'acquired');
  const now = new Date();
  const thisMonth = acquired.filter(customer => {
    const date = new Date(acquiredDate(customer));
    return !Number.isNaN(date.getTime()) && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  });
  const purchaseTotal = acquired.reduce((sum, customer) => sum + money(customer.purchasePrice), 0);
  const retailTotal = acquired.reduce((sum, customer) => sum + money(customer.expectedRetailValue), 0);
  const grossTotal = acquired.reduce((sum, customer) => sum + (money(customer.expectedGross) || Math.max(0, money(customer.expectedRetailValue) - money(customer.purchasePrice))), 0);
  const dayValues = acquired.map(customer => customer.daysToAcquire ?? daysBetween(customer.createdAt, acquiredDate(customer))).filter(value => value !== null && Number.isFinite(Number(value)));
  const avgDays = dayValues.length ? Math.round(dayValues.reduce((a,b) => a + Number(b), 0) / dayValues.length) : 0;
  const sydneyCount = acquired.filter(customer => getAttribution(customer).toLowerCase().includes('sydney')).length;

  root.innerHTML = `
    <div class="vc-roi-head"><div><div class="vc-roi-kicker">Acquisition Performance</div><div class="vc-roi-title">Acquired & ROI</div><div class="vc-roi-note">Only vehicles actually marked Acquired are counted.</div></div><button class="vc-roi-refresh">Refresh ROI</button></div>
    <div class="vc-roi-grid">
      ${metricCard('Acquired This Month', thisMonth.length, `${acquired.length} all time`)}
      ${metricCard('Purchase Investment', formatMoney(purchaseTotal))}
      ${metricCard('Expected Retail', formatMoney(retailTotal))}
      ${metricCard('Expected Gross', formatMoney(grossTotal))}
      ${metricCard('Avg Days to Acquire', avgDays || '—', `${sydneyCount} Sydney attributed`)}
    </div>
    <div class="vc-roi-units">
      ${acquired.length ? acquired.slice().sort((a,b) => new Date(acquiredDate(b)) - new Date(acquiredDate(a))).slice(0,20).map(customer => {
        const gross = money(customer.expectedGross) || Math.max(0,money(customer.expectedRetailValue)-money(customer.purchasePrice));
        return `<div class="vc-roi-row"><div><strong>${vehicleName(customer)}</strong><div class="vc-roi-muted">${customer.name || ''}</div></div><div><span class="vc-roi-muted">Buy</span><br>${formatMoney(customer.purchasePrice)}</div><div><span class="vc-roi-muted">Retail</span><br>${formatMoney(customer.expectedRetailValue)}</div><div><span class="vc-roi-muted">Gross</span><br>${formatMoney(gross)}</div><div><span class="vc-roi-muted">Source</span><br>${customer.source || '—'}</div><div><span class="vc-roi-muted">Days</span><br>${customer.daysToAcquire ?? daysBetween(customer.createdAt,acquiredDate(customer)) ?? '—'}</div><div><span class="vc-roi-muted">Attribution</span><br>${getAttribution(customer)}</div><button class="vc-roi-btn" data-roi-edit="${customer.id}">Edit ROI</button></div>`;
      }).join('') : `<div style="padding:18px 8px;color:rgba(255,255,255,.38);font-size:11px">No acquired vehicles yet. Once an opportunity is marked Acquired, it will appear here.</div>`}
    </div>`;

  root.querySelector('.vc-roi-refresh').onclick = refresh;
  root.querySelectorAll('[data-roi-edit]').forEach(button => {
    button.onclick = () => {
      const id = Number(button.dataset.roiEdit);
      const customer = customersCache.find(item => Number(item.id) === id);
      if (customer) renderEditor(customer);
    };
  });
};

const refresh = async () => {
  const workspace = document.querySelector('.vc-acquisition-workspace');
  if (!workspace) return;
  try {
    customersCache = await fetchCustomers();
    renderROI();
  } catch (error) {
    console.warn('VinCentrix ROI refresh failed:', error);
  }
};

const boot = () => {
  ensureStyles();
  const observer = new MutationObserver(() => {
    if (document.querySelector('.vc-acquisition-workspace') && !document.getElementById('vc-acquisition-roi')) refresh();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  refreshTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && document.querySelector('.vc-acquisition-workspace')) refresh();
  }, 60000);
  window.addEventListener('focus', () => {
    if (document.querySelector('.vc-acquisition-workspace')) refresh();
  });
  refresh();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
